import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';

import { AiOff, Card, Loaded, NotSynced, PageHead, RangeSeg, Select } from '@/components/ui';
import { useApi, useMeta } from '@/lib/api';
import { fmtDay, fmtInt, fmtPct } from '@/lib/format';
import { RANGES, useParam } from '@/lib/params';
import type { Journeys } from '@/lib/types';

function Body({ d }: { d: Journeys }) {
  const navigate = useNavigate();
  const signedUp = d.funnel[0]?.count ?? 0;
  const max = Math.max(1, signedUp);
  return (
    <>
      <div className="row-a" style={{ marginTop: 0, gridTemplateColumns: 'minmax(0,1.4fr) minmax(0,1fr)' }}>
        <Card
          title="Activation funnel"
          right={
            d.biggest_drop && (
              <span className="faint" style={{ fontSize: 12 }}>
                biggest drop: {d.funnel.find((s) => s.key === d.biggest_drop)?.label}
              </span>
            )
          }
        >
          <div className="funnel">
            {d.funnel.map((s) => (
              <div key={s.key} className="st">
                <div>
                  <b>{s.label}</b>
                  {s.median_days !== null && (
                    <div className="faint" style={{ fontSize: 12 }}>
                      median {s.median_days} {s.median_days === 1 ? 'day' : 'days'}
                    </div>
                  )}
                </div>
                <div>
                  <div className="fb" style={{ width: `${Math.max(4, (s.count / max) * 100)}%` }}>
                    {fmtInt(s.count)}
                    {s.conversion !== null && ` · ${fmtPct(s.conversion)}`}
                  </div>
                </div>
                <div className="drop">
                  {s.key === 'active_week4' ? (
                    `${fmtPct(signedUp ? s.count / signedUp : null)} of sign-ups`
                  ) : s.dropped ? (
                    s.key === d.biggest_drop ? (
                      <b>{s.dropped} dropped · biggest</b>
                    ) : (
                      `${s.dropped} dropped`
                    )
                  ) : (
                    '—'
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="note">Connected data, first forecast and first widget come from ActivityEvents.</div>
        </Card>
        <Card
          title={d.stuck.stage_label ? `Stuck before “${d.stuck.stage_label}”` : 'Stuck companies'}
          right={<span className="faint">{fmtInt(d.stuck.total)}</span>}
        >
          {d.stuck.items.length === 0 ? (
            <div className="muted">Nobody is stuck.</div>
          ) : (
            <table>
              <tbody>
                {d.stuck.items.map((c) => (
                  <tr key={c.company_id} onClick={() => navigate(`/accounts/${c.company_id}`)}>
                    <td>
                      <b>{c.name}</b>
                      <div className="faint" style={{ fontSize: 11 }}>
                        day {c.days_since_signup} · {fmtInt(c.asks)} asks
                        {c.trial_expires_at && ` · trial ends ${format(parseISO(c.trial_expires_at), 'd MMM')}`}
                      </div>
                    </td>
                  </tr>
                ))}
                {d.stuck.total > d.stuck.items.length && (
                  <tr>
                    <td className="muted">{d.stuck.total - d.stuck.items.length} more</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
          <div className="note">Why each company is stuck needs AI analysis, which is off.</div>
        </Card>
      </div>

      <Card
        style={{ marginTop: 12 }}
        title="Weekly retention by sign-up cohort"
        right={
          <span className="faint" style={{ fontSize: 12 }}>
            % of the cohort active in that week
          </span>
        }
      >
        {d.cohorts.length === 0 ? (
          <div className="muted">No companies signed up in this range.</div>
        ) : (
          <div className="cohort">
            <span className="h l">Cohort</span>
            <span className="h">Cos</span>
            {Array.from({ length: 8 }, (_, i) => (
              <span key={i} className="h">
                W{i}
              </span>
            ))}
            {d.cohorts.map((c) => (
              <CohortRow key={c.week_start} label={fmtDay(c.week_start)} companies={c.companies} weeks={c.weeks} />
            ))}
          </div>
        )}
      </Card>

      <div className="g2">
        <Card title="Onboarding checklist completion">
          <NotSynced what="Onboarding steps" />
        </Card>
        <Card title="First thing new companies ask">
          <AiOff what="First thing asked" />
        </Card>
      </div>
    </>
  );
}

function CohortRow({ label, companies, weeks }: { label: string; companies: number; weeks: (number | null)[] }) {
  return (
    <>
      <span className="l">{label}</span>
      <span>{companies}</span>
      {weeks.map((p, i) =>
        p === null ? (
          <span key={i} />
        ) : (
          <span
            key={i}
            title={`${fmtPct(p)} active`}
            style={{
              background: `rgb(var(--heat) / ${0.08 + 0.82 * p})`,
              color: p > 0.55 ? 'var(--panel)' : 'var(--ink)',
            }}
          >
            {fmtPct(p)}
          </span>
        ),
      )}
    </>
  );
}

export function JourneysPage() {
  const meta = useMeta();
  const [range] = useParam('range', '90d');
  const [plan, setPlan] = useParam('plan', '');
  const query = useApi<Journeys>('/journeys', { range, plan });
  const w = query.data?.window;
  return (
    <div className="page">
      <PageHead
        title="Journeys"
        subtitle={
          w
            ? `How new companies get from sign-up to regular use · companies created ${fmtDay(
                query.data?.tracking_since && parseISO(query.data.tracking_since) > parseISO(w.start)
                  ? query.data.tracking_since
                  : w.start,
              )} – ${fmtDay(w.end)}${
                query.data?.tracking_since ? ` · activity tracked since ${fmtDay(query.data.tracking_since)}` : ''
              }`
            : ' '
        }
      >
        <RangeSeg options={RANGES.journeys} fallback="90d" />
        <Select
          value={plan}
          active={Boolean(plan)}
          onChange={(v) => setPlan(v || null)}
          options={[
            { value: '', label: 'All plans' },
            ...(meta.data?.plans ?? []).map((p) => ({ value: p, label: p })),
          ]}
        />
      </PageHead>
      <Loaded query={query}>{(d) => <Body d={d} />}</Loaded>
    </div>
  );
}
